import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Project } from 'models/ProjectModel';
import { Stage } from 'models/StageModel';
import AsyncSelect from 'react-select/async';
import { useSearchParams } from 'react-router-dom';
import { Extract } from 'helpers/ExtractHelper';

const ProjectForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.PROJECT, Code.Form.Name.PROJECT];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [employeeOptions, setEmployeeOptions] = Hooks.Form.Options();
  const [allEmployeeList, setAllEmployeeList] = Hooks.Form.Options();
  const [selectedEmployee, setSelectedEmployee] = useState<string[]>([]);
  const [cityOptions, setCityOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Project>({});
  const [stageData, setStageData] = useState<Stage[]>([]);
  const [localStatusId, setLocalStatusId] = useState<Number>(0);
  const [localComment, setLocalComment] = useState<string>('');

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);

  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.OPTION+Code.Group.PROJECT_TYPE, Code.Option.Common.SELECT, setTypeOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.PROJECT_STATUS, Code.Option.Common.SELECT, setStatusOptions, setError);
    Files.Json.API(Code.File.CITY, Code.Option.Common.NA, setCityOptions, setError);
    Files.Json.API(Code.File.EMPLOYEE, Code.Option.Common.NA, setEmployeeOptions, setError);
    Files.Json.API(Code.File.EMPLOYEE, Code.Option.Common.NOTHING, setAllEmployeeList, setError);

    API.Data.GetByKeyId<Project>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.project_active); 
        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData, 
        }));
        setSelectedEmployee(JSON.parse(data.project_employee_list!));

        setLocalStatusId(data.status_id!);
        setLocalComment(data.project_comment!);
        
        callStageList(data.project_id);
        setLoading(false);

      }})

      const onNewEntry = async () => {
        if(keyId===Code.Key.NEW && clientId) {
          const clientName = await API.Data.GetClientName(Number(clientId));
          setFormData((prevData) => ({
            ...prevData,
            client_id: Number(clientId),
            client_name: Utility.Value.Text(clientName), 
          }));
        }
        setLoading(false);
      };
      onNewEntry();

  }, []);

  const callStageList = async (projectId: number | undefined) => {
      if (!projectId) return;  
        const url = API.Build.FormApiEndpint(Code.Form.Name.STAGE, 0, Name.Stage.Id, Code.Option.Sort.DESC, {
        fields: Utility.Join.Fields([
          Name.Stage.Date, 
          Name.Common.StatusName, 
          Name.Stage.Comment,
          Name.User.FullName            
        ]),
        [Name.Common.CommonId]: projectId,
      });
      await API.Data.List<Stage>(url, setStageData, setError, setLoading);
   };

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;

    const comment = formData.project_comment ? formData.project_comment : '';

    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      project_comment:'',
      project_employee_list: JSON.stringify(selectedEmployee),
    });

    const response = await API.Data.Save(formName, keyId, finalData, setMessage, true, Code.Form.PopupClose.NONE);
    if(response) {
      //console.log(">>>>>>>>>>>>>>>", response, keyId);
      if(finalData.status_id !== localStatusId || comment !== localComment) {
        let newKeyId: number | undefined = undefined;
        if(keyId === Code.Key.NEW) {
          const newProjectId = Extract.Key.Decode(response.data?.newid);;
          //console.log("New Project ID>>>>", newProjectId);
          if(newProjectId > Code.Option.Common.NA) {
            newKeyId = newProjectId;
          }
        }else{
          newKeyId = finalData.project_id;
        }
        
        if(newKeyId){
            const stageData: Stage = {
              company_id:Utility.Storage.CompanyId(),
              location_id:Utility.Storage.LocationId(),
              user_id:Utility.Storage.UserId(),
              stage_date: Utility.Date.Current(),
              form_id: formId,
              common_id: newKeyId,
              status_id: finalData.status_id,
              stage_comment: comment,
            }

            const response = await API.Data.Save(Code.Form.Name.STAGE, Code.Key.NEW, stageData, setMessage);
            if(response){
              //do nothing
            }
        }
      }
   
    }
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details","Progress","Recall","Contact","Team"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,32,1]} require={true} />
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,36,1]} require={true}  />
                <Field.Date id={Name.Project.Date} value={formData.project_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,32,0]}  require={true} currentDate={true} />
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Project.Name} value={formData.project_name} title={Title.ProjectName} onChange={(e) => Form.Field.Change(e, formData, setFormData)}  require={true} />
              </Control.Box>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Project.Comment} value={formData.project_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Date id={Name.Project.StartDate} value={formData.project_start_date} title={Title.StartDate} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Date id={Name.Project.EndDate} value={formData.project_end_date} title={Title.EndDate} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Project.Number} value={formData.project_number} title={Title.SNo} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Project.RefNo} value={formData.project_ref_number} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              {stageData.map((stage, index) => { return (
                <div className='stage-list'>
                  <div>
                    <p><strong>{stage.status_name}</strong> on {Utility.Format.DateOnly(stage.stage_date)}</p>
                    <span>{Utility.Get.FullName(stage,Code.Form.Name.USER)}</span>
                  </div>
                  <div>{stage.stage_comment}</div>
                </div>
              )})}
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <>Recall</>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab4} isActive={activeTab === Code.Tab.Tab4}>
              <Control.Box>
                <Field.Text id={Name.Project.Person} value={formData.project_person} title="Contact person" onChange={(e) => Form.Field.Change(e, formData, setFormData)}  />
              </Control.Box>
              <Control.Row>
                <Field.Text id={Name.Insurer.Phone} value={formData.project_phone} title={Title.Phone} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Text id={Name.Insurer.Email} value={formData.project_email} title={Title.Email} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.User.Address} value={formData.project_address} title={Title.Address} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.City.Id} value={formData.city_id} title={Title.City} options={cityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]}  />
                <Field.Text id={Name.User.Postal} value={formData.project_postal} title={Title.Postal} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab5} isActive={activeTab === Code.Tab.Tab5}>
              <Control.Box>
                <Field.FilterSelect id={Name.Employee.Id} value={formData.employee_id} title={Title.Team} options={employeeOptions} setFormData={setFormData} />
                <Field.MultiSelect title="Assigned Team" id={Title.Team} options={allEmployeeList} selectedItems={selectedEmployee} setSelectedItems={setSelectedEmployee}/>
              </Control.Box>
            </Control.Tab.Content>
            
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default ProjectForm;